import { kebabCase } from 'lodash';
import React from 'react';
import { ImageOptionalLink } from '../../../../commonTypes/links';
import { generateKeyForElement } from '../../../../helpers/elementKeyHelper';
import Collapsible from '../../../Collapsible';
import { Grid } from '../../../Grid';
import GridContainer from '../../../GridContainer';
import { combineTertiaryNavItems } from '../../helpers';
import { SecondaryNav, TertiaryNavItem, TertiaryNavItemWithIcon } from '../../types';
import { useActiveItems } from '../../useActiveItems';
import LanguageToggle, { LanguageToggleProps } from '../LanguageToggle';
import TertiaryNavItemComponent from './TertiaryNavItem';
import {
  CtaContainer,
  HeaderInner,
  Logo,
  NavItemWrapper,
  RightAlignedGridItem,
  SecondaryMenu,
  StyledGridItem,
  StyledSecondaryNavMenu,
  StyledTertiaryButtonWithMargin,
} from './styles';

export type TopMenuProps = {
  logoLink: ImageOptionalLink;
  secondaryNav?: SecondaryNav;
  /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
  tertiaryNavItem?: TertiaryNavItem | TertiaryNavItemWithIcon;
  /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
  secondTertiaryNavItem?: TertiaryNavItem | TertiaryNavItemWithIcon;

  tertiaryNavItems?: (TertiaryNavItem | TertiaryNavItemWithIcon)[];
  languageToggle?: LanguageToggleProps;
  resetNav: () => void;
  activeItemIndex: number | null;
  setActiveItemIndex: (index: number | null) => void;
};

const getSecondaryMenuId = (name: string): string => `${kebabCase(name)}-menu`;

const getGridColumnsSpanned = (
  secondaryNav?: SecondaryNav,
  tertiaryNavItems?: (TertiaryNavItem | TertiaryNavItemWithIcon)[],
  languageToggle?: LanguageToggleProps
): {
  logo: { desktop: number; tablet: number };
  ctas: { desktop: number; tablet: number };
} => {
  // Only the first secondary nav item is rendered, as the space won't allow for more
  const numberOfNavItems =
    (secondaryNav?.navItems[0] ? 1 : 0) +
    (tertiaryNavItems ? tertiaryNavItems.length : 0) +
    (languageToggle ? 1 : 0);

  return numberOfNavItems > 2
    ? {
        logo: {
          desktop: 5,
          tablet: 4,
        },
        ctas: {
          desktop: 7,
          tablet: 8,
        },
      }
    : {
        logo: {
          desktop: 8,
          tablet: 7,
        },
        ctas: {
          desktop: 4,
          tablet: 5,
        },
      };
};

const TopMenu: React.FC<TopMenuProps> = ({
  logoLink,
  secondaryNav,
  tertiaryNavItem,
  secondTertiaryNavItem,
  tertiaryNavItems,
  languageToggle,
  resetNav,
  ...activeItemProps
}) => {
  const combinedTertiaryNavItems = combineTertiaryNavItems(
    tertiaryNavItem,
    secondTertiaryNavItem,
    tertiaryNavItems
  );

  const [{ activeItemIndex }, onClickNavItem] = useActiveItems(
    activeItemProps.activeItemIndex,
    activeItemProps.setActiveItemIndex
  );

  const onClick = (event: React.MouseEvent): void => {
    if (secondaryNav?.navItems[0].onExpand && activeItemIndex !== 0) {
      secondaryNav.navItems[0].onExpand(event);
    }
    if (secondaryNav?.navItems[0].onCollapse && activeItemIndex === 0) {
      secondaryNav.navItems[0].onCollapse(event);
    }
    onClickNavItem(0)();
  };

  const gridColumnsSpanned = getGridColumnsSpanned(
    secondaryNav,
    combinedTertiaryNavItems,
    languageToggle
  );

  return (
    <HeaderInner>
      <GridContainer>
        <Grid>
          <StyledGridItem
            desktop={gridColumnsSpanned.logo.desktop}
            tabletLandscape={gridColumnsSpanned.logo.tablet}>
            <Logo logoLink={logoLink} shouldEagerLoad={true} />
          </StyledGridItem>
          <RightAlignedGridItem
            desktop={gridColumnsSpanned.ctas.desktop}
            tabletLandscape={gridColumnsSpanned.ctas.tablet}>
            <CtaContainer>
              {combinedTertiaryNavItems.length > 0 && (
                <TertiaryNavItemComponent navItem={combinedTertiaryNavItems[0]} />
              )}
              {/* Only the first secondary nav item is rendered here, as the space won't allow for more */}
              {secondaryNav?.navItems[0] && (
                <NavItemWrapper>
                  <StyledTertiaryButtonWithMargin background="dark" onClick={onClick}>
                    {secondaryNav.navItems[0].name}
                  </StyledTertiaryButtonWithMargin>
                  <SecondaryMenu>
                    <Collapsible
                      isOpened={activeItemIndex === 0}
                      id={getSecondaryMenuId(secondaryNav.navItems[0].name)}>
                      <StyledSecondaryNavMenu
                        navItem={secondaryNav.navItems[0]}
                        resetNav={resetNav}
                      />
                    </Collapsible>
                  </SecondaryMenu>
                </NavItemWrapper>
              )}
              {combinedTertiaryNavItems.slice(1).map((navItem) => (
                <TertiaryNavItemComponent
                  key={generateKeyForElement(navItem)}
                  navItem={navItem}
                />
              ))}
              {languageToggle && (
                <NavItemWrapper>
                  <LanguageToggle {...languageToggle} />
                </NavItemWrapper>
              )}
            </CtaContainer>
          </RightAlignedGridItem>
        </Grid>
      </GridContainer>
    </HeaderInner>
  );
};

export default TopMenu;
